import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Lock } from 'lucide-react';
import '../styles/enterprise.css';
import { useTheme } from '../context/ThemeContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import RequireFeature from '../components/common/RequireFeature';
import PageHeaderPro from '../components/common/PageHeaderPro';
import KpiStrip from '../components/common/KpiStrip';
import KpiCard from '../components/common/KpiCard';
import {
  useGetAnalyticsOverviewQuery,
  useGetAnalyticsBreakdownQuery,
} from '../store/api/analyticsApi';
import { formatUSD, formatCount } from '../utils/formatAmount';

const RANGES = [
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
  { key: '12m', label: '12M' },
];

// Categorical palette for the pie / multi-series charts.
const PIE_COLORS = ['#635bff', '#00b884', '#f5a623', '#eb5757', '#2d9cdb', '#9b51e0', '#56ccf2', '#bb6bd9'];

const panelStyle = {
  background: 'var(--ent-card)',
  border: '1px solid var(--ent-border)',
  borderRadius: 'var(--ent-radius)',
  boxShadow: 'var(--sh-card)',
  padding: '16px 18px',
};
const panelTitleStyle = {
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--ent-text)',
  marginBottom: 12,
};
const gridStyleFor = (isMobile) => ({
  display: 'grid',
  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
  gap: 14,
  marginTop: 18,
});

// Format a bucket key (YYYY-MM-DD or YYYY-MM-01) for the x-axis.
// Success-rate panel: headline row and outcome colours.
const SUCCESS_GREEN = '#00b884';
const FAIL_RED = '#f4476b';
const rateSummaryStyle = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  marginBottom: 10,
};
const rateBigStyle = {
  fontSize: 26,
  fontWeight: 700,
  lineHeight: 1,
  color: 'var(--ent-text)',
  fontVariantNumeric: 'tabular-nums',
  marginRight: 4,
};
const ratePillStyle = (color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ent-text)',
  background: `${color}14`,
});
const rateDotStyle = (color) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: color,
  display: 'inline-block',
});

const fmtBucket = (b) => {
  if (!b) return '';
  const [, m, d] = b.split('-');
  return `${m}/${d}`;
};

const LockedPanel = ({ navigate }) => (
  <div className="ent-page">
    <PageHeaderPro title="Advanced Analytics" mode="live" />
    <div style={{ ...panelStyle, textAlign: 'center', padding: '48px 24px' }}>
      <Lock size={32} style={{ color: 'var(--ent-muted)', marginBottom: 12 }} />
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
        Advanced analytics is a Premium feature
      </h2>
      <p style={{ color: 'var(--ent-muted)', marginBottom: 20 }}>
        Upgrade to Premium to unlock revenue trends, success-rate tracking, and
        token / chain breakdowns.
      </p>
      <button
        className="ent-btn ent-btn-primary"
        onClick={() => navigate('/dashboard/billing')}
      >
        Upgrade to Premium
      </button>
    </div>
  </div>
);

const EmptyPanel = ({ height = 260 }) => (
  <div
    style={{
      height,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--ent-muted)',
      fontSize: 14,
    }}
  >
    No data for this range
  </div>
);

const AnalyticsContent = () => {
  const navigate = useNavigate();
  const { mode } = useTheme();
  const isMobile = useIsMobile();
  const isTestnet = mode === 'test' ? 'true' : 'false';
  const [range, setRange] = useState('30d');

  const overviewQuery = useGetAnalyticsOverviewQuery({ range, isTestnet });
  const breakdownQuery = useGetAnalyticsBreakdownQuery({ range, isTestnet });

  // Surface the locked panel if the API gates us (non-Premium reaching the call).
  const lockedError =
    overviewQuery.error?.data?.error === 'feature_locked' ||
    breakdownQuery.error?.data?.error === 'feature_locked';
  if (lockedError) {
    return <LockedPanel navigate={navigate} />;
  }

  const isLoading = overviewQuery.isLoading || breakdownQuery.isLoading;
  const overview = overviewQuery.data;
  const breakdown = breakdownQuery.data;

  const totals = overview?.totals || {
    revenueUsd: 0,
    txCount: 0,
    succeeded: 0,
    successRate: null,
  };
  const series = overview?.series || [];
  const hasSeries = series.some((p) => p.txCount > 0);

  const avgTicket =
    totals.succeeded > 0 ? totals.revenueUsd / totals.succeeded : 0;

  // Success-rate chart window. A rate exists only on days with a succeeded or
  // failed payment, so a 30-day axis with two active days on the far right
  // read as an empty chart. It starts at the first active day instead, keeping
  // at least a week on the axis so a single day still has context.
  const MIN_RATE_BUCKETS = 7;
  const firstActive = series.findIndex((p) => (p.succeeded || 0) + (p.failed || 0) > 0);
  const rateHasData = firstActive !== -1;
  const rateSeries = rateHasData
    ? series.slice(Math.max(0, Math.min(firstActive, series.length - MIN_RATE_BUCKETS)))
    : [];

  const rangeToggle = (
    <div style={{ display: 'flex', gap: 6 }} role="group" aria-label="Date range">
      {RANGES.map((r) => (
        <button
          key={r.key}
          className={`ent-btn ${range === r.key ? 'ent-btn-primary' : 'ent-btn-secondary'}`}
          onClick={() => setRange(r.key)}
          aria-pressed={range === r.key}
        >
          {r.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="ent-page">
      <PageHeaderPro
        title="Advanced Analytics"
        mode={mode === 'test' ? 'test' : 'live'}
        actions={rangeToggle}
      />

      <KpiStrip>
        <KpiCard label="REVENUE" value={formatUSD(totals.revenueUsd)} sub={`${range} settled`} />
        <KpiCard label="TRANSACTIONS" value={formatCount(totals.txCount)} />
        <KpiCard
          label="SUCCESS RATE"
          value={totals.successRate != null ? `${totals.successRate}%` : '—'}
        />
        <KpiCard label="AVG TICKET" value={formatUSD(avgTicket)} sub="per succeeded" />
      </KpiStrip>

      {isLoading ? (
        <div style={{ ...panelStyle, marginTop: 18 }}>
          <EmptyPanel height={120} />
          <div style={{ textAlign: 'center', color: 'var(--ent-muted)' }}>Loading…</div>
        </div>
      ) : (
        <div style={gridStyleFor(isMobile)} className="analytics-grid">
          <div style={panelStyle}>
            <div style={panelTitleStyle}>Revenue (USD)</div>
            {hasSeries ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--ent-border)" />
                  <XAxis dataKey="bucket" tickFormatter={fmtBucket} fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip formatter={(v) => formatUSD(v)} labelFormatter={fmtBucket} />
                  <Line type="monotone" dataKey="revenueUsd" name="Revenue" stroke="#635bff" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyPanel />
            )}
          </div>

          <div style={panelStyle}>
            <div style={panelTitleStyle}>Transaction volume</div>
            {hasSeries ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--ent-border)" />
                  <XAxis dataKey="bucket" tickFormatter={fmtBucket} fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip labelFormatter={fmtBucket} />
                  <Bar dataKey="txCount" name="Transactions" fill="#635bff" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyPanel />
            )}
          </div>

          <div style={panelStyle}>
            <div style={panelTitleStyle}>Success rate</div>
            {rateHasData ? (
              <>
                {/* The headline: overall rate and the counts behind it. A rate
                    on its own cannot say whether it was 1 payment or 400. */}
                <div style={rateSummaryStyle}>
                  <span style={rateBigStyle}>
                    {totals.successRate != null ? `${totals.successRate}%` : '—'}
                  </span>
                  <span style={ratePillStyle(SUCCESS_GREEN)}>
                    <i style={rateDotStyle(SUCCESS_GREEN)} />
                    {formatCount(totals.succeeded || 0)} succeeded
                  </span>
                  <span style={ratePillStyle(FAIL_RED)}>
                    <i style={rateDotStyle(FAIL_RED)} />
                    {formatCount(totals.failed || 0)} failed
                  </span>
                </div>

                {/* Bars for the daily outcome counts, the rate as a line over
                    them. A bare rate line was a sliver between the few days with
                    payments and drew nothing at all for a single active day;
                    the bars give every active day a solid, visible mark. */}
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={rateSeries} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--ent-border)" vertical={false} />
                    <XAxis dataKey="bucket" tickFormatter={fmtBucket} fontSize={11} tickLine={false} />
                    <YAxis
                      yAxisId="count"
                      allowDecimals={false}
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      width={28}
                    />
                    <YAxis
                      yAxisId="rate"
                      orientation="right"
                      domain={[0, 100]}
                      ticks={[0, 50, 100]}
                      tickFormatter={(v) => `${v}%`}
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />
                    <Tooltip
                      labelFormatter={fmtBucket}
                      formatter={(v, name) =>
                        name === 'Success rate'
                          ? [v == null ? 'No payments' : `${v}%`, name]
                          : [formatCount(v || 0), name]
                      }
                    />
                    <Bar yAxisId="count" dataKey="succeeded" name="Succeeded" stackId="o" fill={SUCCESS_GREEN} fillOpacity={0.85} maxBarSize={22} />
                    <Bar yAxisId="count" dataKey="failed" name="Failed" stackId="o" fill={FAIL_RED} fillOpacity={0.85} maxBarSize={22} radius={[3, 3, 0, 0]} />
                    <Line
                      yAxisId="rate"
                      type="monotone"
                      dataKey="successRate"
                      name="Success rate"
                      stroke="#5b4ee0"
                      strokeWidth={2}
                      dot={{ r: 3.5, fill: '#5b4ee0', stroke: '#ffffff', strokeWidth: 1.5 }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </>
            ) : (
              <EmptyPanel />
            )}
          </div>

          <div style={panelStyle}>
            <div style={panelTitleStyle}>Top tokens (settled USD)</div>
            {breakdown?.topTokens?.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={breakdown.topTokens}
                  layout="vertical"
                  margin={{ top: 8, right: 12, left: 8, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--ent-border)" />
                  <XAxis type="number" fontSize={11} />
                  <YAxis type="category" dataKey="token" fontSize={11} width={64} />
                  <Tooltip formatter={(v) => formatUSD(v)} />
                  <Bar dataKey="revenueUsd" name="Revenue" fill="#635bff" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyPanel />
            )}
          </div>

          <div style={panelStyle}>
            <div style={panelTitleStyle}>Top chains (settled USD)</div>
            {breakdown?.topChains?.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={breakdown.topChains}
                    dataKey="revenueUsd"
                    nameKey="chain"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={(e) => e.chain}
                  >
                    {breakdown.topChains.map((entry, i) => (
                      <Cell key={entry.chain} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatUSD(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyPanel />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Feature-gated wrapper. The page itself is reachable, but the content is locked
// behind the `advanced_analytics` Premium feature with a self-service upgrade CTA.
const Analytics = () => {
  const navigate = useNavigate();
  return (
    <RequireFeature
      feature="advanced_analytics"
      fallback={<LockedPanel navigate={navigate} />}
    >
      <AnalyticsContent />
    </RequireFeature>
  );
};

export default Analytics;
